/**
 * FontUploadModal — upload a new .ttf / .otf font to the library.
 */

import { useState } from 'react'
import { useFontUpload } from '../hooks/useFonts'
import type { HttpError } from '@/lib/api/client'

interface Props {
  open: boolean
  onClose: () => void
}

export function FontUploadModal({ open, onClose }: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const upload = useFontUpload()

  if (!open) return null

  const handleFile = (f: File) => {
    setFile(f)
    if (!displayName) {
      const stem = f.name.replace(/\.[^.]+$/, '')
      setDisplayName(stem)
    }
  }

  const resetForm = () => {
    setFile(null)
    setDisplayName('')
    setDescription('')
    upload.reset()
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    if (!file || !displayName) return
    try {
      await upload.mutateAsync({ file, display_name: displayName, description: description || undefined })
      resetForm()
      onClose()
    } catch {
      // error surfaced via upload.isError below
    }
  }

  const errMsg = (() => {
    if (!upload.isError) return null
    const err = upload.error as HttpError | undefined
    const detail = (err?.responseData as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string') return detail
    return err?.message ?? '上传失败'
  })()

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">上传字体</h3>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-gray-700">字体文件 (.ttf / .otf, ≤ 50MB)</label>
            <input
              type="file"
              accept=".ttf,.otf"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              className="mt-1 block w-full text-sm"
            />
            {file && (
              <div className="mt-1 text-xs text-gray-500">
                {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
              </div>
            )}
          </div>
          <div>
            <label className="text-sm text-gray-700">显示名称</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={128}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="text-sm text-gray-700">描述 (可选)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!file || !displayName || upload.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 text-sm"
          >
            {upload.isPending ? '上传中...' : '上传'}
          </button>
        </div>
        {errMsg && <div className="mt-3 text-sm text-red-600">{errMsg}</div>}
      </div>
    </div>
  )
}
